import type { PricingCampaignModel } from '@/shared/generated'

import { find, propEq } from 'lodash/fp'
import {
	EditIcon,
	LayersIcon,
	MoreVerticalIcon,
	PencilIcon,
	Trash2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { DeletePricingCampaign } from '@/features/pricing-campaign/delete'

import {
	EngineBadge,
	GlobalPcBadge,
	isGlobalPricingCampaign,
} from '@/entities/pricing-campaign'
import { Access } from '@/entities/session'

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
} from '@/shared/components/ui/sidebar'
import { ampli } from '@/shared/lib/ampli'

import { useGlobalPcSets } from '../../GlobalPcSetsProvider'
import { DuplicatePricingCampaign } from './DuplicatePricingCampaign'
import { DuplicatePricingCampaignTo } from './DuplicatePricingCampaignTo'
import { EditPricingCampaign } from './EditPricingCampaign'
import { RenamePricingCampaign } from './RenamePricingCampaign'

export const PricingCampaignItem = ({
	pricingCampaign,
}: {
	pricingCampaign: PricingCampaignModel
}) => {
	const { t } = useTranslation()

	const { replaceLastPanel, globalPcSets } = useGlobalPcSets()
	const pricingCampaignId = pricingCampaign.id
	const engine = pricingCampaign.settings.pricing_tactics.engine
	const isGlobal = isGlobalPricingCampaign(pricingCampaign)
	const pricingCampaignGlobalSet = find(
		propEq('id', pricingCampaign.global_pc_set_id),
		globalPcSets
	)
	const isInMainGlobalPcSet = Boolean(pricingCampaignGlobalSet?.is_main)

	return (
		<SidebarMenuItem key={pricingCampaignId}>
			<SidebarMenuButton
				size='sm'
				onClick={() => {
					replaceLastPanel({
						pricingCampaignId,
						part: 'settings',
					})
				}}
				className='cursor-pointer justify-between'
			>
				<span className='truncate'>{pricingCampaign.name}</span>
				<div className='flex items-center gap-1'>
					{isGlobal && <GlobalPcBadge />}
					{!!engine && (
						<EngineBadge engine={engine}>
							{t(engine, { defaultValue: engine })}
						</EngineBadge>
					)}
				</div>
			</SidebarMenuButton>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<SidebarMenuAction className='cursor-pointer'>
						<MoreVerticalIcon />
					</SidebarMenuAction>
				</DropdownMenuTrigger>
				<DropdownMenuContent side='bottom' align='end'>
					<EditPricingCampaign pricingCampaign={pricingCampaign}>
						<DropdownMenuItem disabled={isInMainGlobalPcSet}>
							<EditIcon />
							{t('app.edit', { defaultValue: 'Edit' })}
						</DropdownMenuItem>
					</EditPricingCampaign>
					<RenamePricingCampaign pricingCampaign={pricingCampaign}>
						<DropdownMenuItem disabled={isInMainGlobalPcSet}>
							<PencilIcon />
							{t('app.rename', { defaultValue: 'Rename' })}
						</DropdownMenuItem>
					</RenamePricingCampaign>
					<DuplicatePricingCampaign
						pricingCampaignId={pricingCampaignId}
						globalPcSetId={pricingCampaign.global_pc_set_id!}
					>
						<DropdownMenuItem
							disabled={isInMainGlobalPcSet}
							onClick={() => ampli.pcDuplicateClicked()}
						>
							<LayersIcon />
							{t('general_duplicate')}
						</DropdownMenuItem>
					</DuplicatePricingCampaign>
					<DuplicatePricingCampaignTo pricingCampaign={pricingCampaign}>
						<DropdownMenuItem onClick={() => ampli.pcDuplicateToClicked()}>
							<LayersIcon />
							{t('general_duplicate_to', { defaultValue: 'Duplicate to ...' })}
						</DropdownMenuItem>
					</DuplicatePricingCampaignTo>
					<Access permissions={['DELETE_GLOBAL_PRICING_CAMPAIGNS']}>
						<DeletePricingCampaign pricingCampaignId={pricingCampaignId}>
							<DropdownMenuItem
								disabled={isInMainGlobalPcSet}
								variant='destructive'
								onClick={() => ampli.pcDeleteClicked()}
							>
								<Trash2Icon />
								{t('general_delete')}
							</DropdownMenuItem>
						</DeletePricingCampaign>
					</Access>
				</DropdownMenuContent>
			</DropdownMenu>
		</SidebarMenuItem>
	)
}
